/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import theme from '../theme'
import {RESPONSIVE_IMAGE_BUCKETS, DEFAULT_RESPONSIVE_IMAGE_WIDTH} from '../constants'

export {RESPONSIVE_IMAGE_BUCKETS}

/**
 * Snap a CSS-pixel width to the smallest universal bucket >= input.
 * Returns null for non-positive input (no layout). For input above the largest
 * bucket, returns the largest bucket. DPR is NOT applied here.
 * @param {number} px - CSS pixel width
 * @param {number[]} [buckets] - Bucket array to snap to (defaults to RESPONSIVE_IMAGE_BUCKETS)
 * @return {number|null} Snapped bucket value or null
 */
export const snapToImageBucket = (px, buckets = RESPONSIVE_IMAGE_BUCKETS) => {
    if (!Number.isFinite(px) || px <= 0) return null
    for (const b of buckets) if (b >= px) return b
    return buckets[buckets.length - 1]
}

/**
 * @param {Object} breakpoints
 * @return {string[]} Breakpoint labels ordered from smallest. For example: ['base', 'sm', 'md', 'lg', 'xl', '2xl']
 */
const getBreakpointLabels = (breakpoints) =>
    Object.entries(breakpoints)
        .sort((a, b) => parseFloat(a[1]) - parseFloat(b[1]))
        .map(([key]) => key)

const {breakpoints: defaultBreakpoints} = theme
let themeBreakpoints = defaultBreakpoints
let breakpointLabels = getBreakpointLabels(themeBreakpoints)

/**
 * @param {Object} props
 * @param {string} props.src - Dynamic src having an optional param that can vary with widths. For example: `image[_{width}].jpg` or `image.jpg[?sw={width}&q=60]`
 * @param {(number[]|string[]|Object)} [props.widths] - Image widths relative to the breakpoints, whose units can either be px or vw or unit-less. They will be mapped to the corresponding `sizes` and `srcSet`.
 * @param {Object} [props.breakpoints] - The current theme's breakpoints. If not given, Chakra's default breakpoints will be used.
 * @return {Object} src, sizes, and srcSet props for your image component
 */
export const getResponsiveImageAttributes = ({src, widths, breakpoints = defaultBreakpoints}) => {
    if (!widths) {
        return {
            src: getSrcWithoutOptionalParams(src)
        }
    }

    themeBreakpoints = breakpoints
    breakpointLabels = getBreakpointLabels(themeBreakpoints)

    // Order of these attributes matter! If src is not last, Safari will refetch images
    // multiple times (once when it processes src and again when it processes sizes / srcSet)
    // See https://github.com/SalesforceCommerceCloud/pwa-kit/pull/2223
    return {
        sizes: mapWidthsToSizes(widths),
        srcSet: mapWidthsToSrcSet(widths, src),
        src: getSrcWithoutOptionalParams(src)
    }
}

/**
 * @param {(number[]|string[]|Object)} widths
 * @return {string}
 */
const mapWidthsToSizes = (widths) => {
    const _widths = withUnit(Array.isArray(widths) ? widths : widthsAsArray(widths))

    return breakpointLabels
        .slice(0, _widths.length)
        .map((bp, i) => {
            return i === 0 ? _widths[i] : `(min-width: ${themeBreakpoints[bp]}) ${_widths[i]}`
        })
        .reverse()
        .join(', ')
}

/**
 * Process widths into pixel values with padding
 * @param {(number[]|string[]|Object)} widths
 * @return {number[]} Array of pixel widths (unique, sorted)
 */
const processWidthsToPx = (widths) => {
    let _widths = isObject(widths) ? widthsAsArray(widths) : widths.slice(0)

    if (_widths.length < breakpointLabels.length) {
        const lastWidth = _widths[_widths.length - 1]
        const amountToPad = breakpointLabels.length - _widths.length
        _widths = [..._widths, ...Array(amountToPad).fill(lastWidth)]
    }

    return uniqueArray(convertToPxNumbers(_widths)).sort()
}

/**
 * Add DPR variants and clamp to supported range
 * @param {number[]} widths - Array of pixel widths
 * @return {number[]} Widths with 2x variants, clamped to 10-3000px range
 */
const addDprVariantsAndClamp = (widths) => {
    const widthsWithDpr = []
    widths.forEach((width) => {
        widthsWithDpr.push(width)
        widthsWithDpr.push(width * 2) // for devices with higher pixel density
    })

    return uniqueArray(
        widthsWithDpr
            // limit to SalesForce DIS supported range 10 <= sw <= 3000 and ceil floating numbers
            .map((width) => Math.max(10, Math.min(3000, Math.ceil(width))))
    ).sort((a, b) => a - b)
}

/**
 * @param {(number[]|string[]|Object)} widths
 * @return {string}
 */
const mapWidthsToSrcSet = (widths, dynamicSrc) => {
    const pxWidths = processWidthsToPx(widths)
    const srcSet = addDprVariantsAndClamp(pxWidths)
    return srcSet.map((imageWidth) => `${getSrc(dynamicSrc, imageWidth)} ${imageWidth}w`).join(', ')
}

const vwValue = /^\d+vw$/
const pxValue = /^\d+px$/

/**
 * @param {string[]|number[]} widths
 * @return {number[]}
 */
const convertToPxNumbers = (widths) => {
    return (
        widths
            .map((width, i) => {
                if (typeof width === 'number') {
                    return width
                }

                if (vwValue.test(width)) {
                    const vw = parseFloat(width)
                    const currentBp = breakpointLabels[i]
                    // We imagine the biggest image for the current breakpoint
                    // to be when the viewport is closely approaching the _next breakpoint_.
                    const nextBp = breakpointLabels[i + 1]

                    if (nextBp) {
                        return vwToPx(vw, nextBp)
                    } else {
                        // We're already at the last breakpoint
                        return widths[i] !== widths[i - 1] ? vwToPx(vw, currentBp) : undefined
                    }
                } else if (pxValue.test(width)) {
                    return parseInt(width)
                } else {
                    console.error('Expecting to see values with vw or px unit only')
                    return 0
                }
            })
            .filter((width) => width !== undefined)
            // limit to SalesForce DIS supported range 10 <= sw <= 3000 and ceil floating numbers
            .map((width) => Math.max(10, Math.min(3000, Math.ceil(width))))
    )
}

const uniqueArray = (array) => [...new Set(array)]

/**
 * @param {(number[]|string[])} widths
 */
const withUnit = (widths) =>
    // By default, unitless value is interpreted as px
    widths.map((width) => (typeof width === 'number' ? `${width}px` : width))

const isObject = (o) => o?.constructor === Object

/**
 * @param {Object} widths
 * @example
 * // returns the array [10, 10, 10, 50]
 * widthsAsArray({base: 10, lg: 50})
 */
const widthsAsArray = (widths) => {
    const biggestBreakpoint = breakpointLabels.filter((bp) => Boolean(widths[bp])).pop()

    let mostRecent
    return breakpointLabels.slice(0, breakpointLabels.indexOf(biggestBreakpoint) + 1).map((bp) => {
        if (widths[bp]) {
            mostRecent = widths[bp]
            return widths[bp]
        } else {
            return mostRecent
        }
    })
}

/**
 * @param {number} vw
 * @param {string} breakpoint
 */
const vwToPx = (vw, breakpoint) => {
    let result = (vw / 100) * parseFloat(themeBreakpoints[breakpoint])
    const breakpointsDefinedInPx = Object.values(themeBreakpoints).some((val) => pxValue.test(val))

    // Assumes theme's breakpoints are defined in either em or px
    // See https://chakra-ui.com/docs/features/responsive-styles#customizing-breakpoints
    return breakpointsDefinedInPx ? result : emToPx(result)
}

/**
 * @param {number} em
 * @param {number} [browserDefaultFontSize]
 */
const emToPx = (em, browserDefaultFontSize = 16) => Math.round(em * browserDefaultFontSize)

/**
 * Encode URL while preserving already-encoded sequences and URI structure
 * Uses the URL API for proper parsing and encoding
 * @param {string} url - URL to encode
 * @return {string} Properly encoded URL
 */
export const encodeUrlPath = (url) => {
    if (!url) return url

    try {
        // URL() automatically encodes the URL when accessed via .href
        return new URL(url).href
    } catch {
        // If URL() fails (malformed URL with spaces), encode the path first
        const queryIndex = url.indexOf('?')
        const path = queryIndex === -1 ? url : url.substring(0, queryIndex)
        const queryString = queryIndex === -1 ? '' : url.substring(queryIndex)
        const encodedUrl = encodeURI(path) + queryString

        try {
            // Try parsing again after encoding
            return new URL(encodedUrl).href
        } catch {
            // Fallback for relative URLs or other edge cases
            return encodedUrl
        }
    }
}

/**
 * @param {string} dynamicSrc
 * @param {number} imageWidth
 * @return {string} Image url having the given width
 * @example
 * // returns https://example.com/image_720.jpg
 * getSrc('https://example.com/image[_{width}].jpg', 720)
 */
export const getSrc = (dynamicSrc, imageWidth) => {
    // 1. remove the surrounding []
    // 2. replace {...} with imageWidth
    // 3. replace all ? with &
    // 4. replace first & with ?
    const url = dynamicSrc
        .replace(/\[([^\]]+)\]/g, '$1')
        .replace(/\{[^}]+\}/g, imageWidth)
        .replace(/\?/g, '&')
        .replace(/&/, '?')

    // 5. encode spaces in the URL path
    return encodeUrlPath(url)
}

/**
 * Generate sizes attribute string from responsive widths configuration
 * @param {Object} options
 * @param {(number[]|string[]|Object)} options.widths - Image widths relative to breakpoints
 * @param {Object} [options.breakpoints] - Theme breakpoints
 * @return {string} Sizes attribute string for use in <source> or <img> tags
 *
 * @example
 * getSizesAttribute({widths: [400, 800, 1200]})
 * // Returns: "(min-width: 768px) 800px, (min-width: 1280px) 1200px, 400px"
 *
 * @example
 * getSizesAttribute({widths: {base: '90vw', s: '50vw'}})
 * // Returns: "(min-width: 1280px) 50vw, 90vw"
 */
export const getSizesAttribute = ({widths, breakpoints = defaultBreakpoints}) => {
    if (!widths) {
        return DEFAULT_RESPONSIVE_IMAGE_WIDTH
    }

    themeBreakpoints = breakpoints
    breakpointLabels = getBreakpointLabels(themeBreakpoints)

    return mapWidthsToSizes(widths)
}

/**
 * Get bucketed pixel widths from responsive widths configuration.
 * RESPONSIVE_IMAGE_BUCKETS are final physical pixel widths (DPR already included),
 * so they are returned as-is without 2x doubling.
 * @param {Object} options
 * @param {(number[]|string[]|Object)} [options.widths] - Image widths relative to breakpoints (omit to include all buckets)
 * @param {Object} [options.breakpoints] - Theme breakpoints
 * @return {number[]} Array of pixel widths for srcset generation
 *
 * @example
 * getBucketedWidths({widths: [100, 200, 300]})
 * // Returns: [240, 768] (buckets up to the snapped max)
 *
 * @example
 * getBucketedWidths({}) // no widths
 * // Returns: all RESPONSIVE_IMAGE_BUCKETS
 */
export const getBucketedWidths = ({widths, breakpoints = defaultBreakpoints}) => {
    themeBreakpoints = breakpoints
    breakpointLabels = getBreakpointLabels(themeBreakpoints)

    // Determine candidate set from RESPONSIVE_IMAGE_BUCKETS
    let candidates = [...RESPONSIVE_IMAGE_BUCKETS]

    // Optional upper-bound clip from widths for HTML-size hygiene
    if (widths) {
        // Process widths to px values
        const pxWidths = processWidthsToPx(widths)

        // Find max px value, then scale for 2x DPR — buckets are physical pixels,
        // so a 200px CSS element needs up to 400px on a Retina display.
        const maxCssPx = Math.max(...pxWidths)

        // Snap to bucket and clip there (ensures we include the bucket that maxCssPx snaps to)
        const maxCandidate = snapToImageBucket(maxCssPx * 2)
        candidates = RESPONSIVE_IMAGE_BUCKETS.filter((b) => b <= maxCandidate)

        // Always include at least the smallest bucket
        if (candidates.length === 0) {
            candidates = [RESPONSIVE_IMAGE_BUCKETS[0]]
        }
    }

    // Buckets are already final physical pixel widths — clamp to DIS range only
    return uniqueArray(candidates.map((width) => Math.max(10, Math.min(3000, width)))).sort(
        (a, b) => a - b
    )
}

/**
 * @param {string} dynamicSrc
 * @example
 * // Returns 'https://example.com/image.jpg'
 * getSrcWithoutOptionalParams('https://example.com/image.jpg[?sw={width}]')
 */
const getSrcWithoutOptionalParams = (dynamicSrc) => {
    const optionalParams = /\[[^\]]+\]/g
    return dynamicSrc.replace(optionalParams, '')
}
